/**
 * ProposalHero - Cabeçalho premium da landing pública de propostas
 *
 * Concentra identidade do fornecedor, título da proposta, ancoragem de valor
 * (total em destaque), urgência de validade e os CTAs primários.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (redesign premium da landing /p/:token)
 */

import React from 'react';
import {
  CalendarClock,
  CheckCircle2,
  Download,
  FileText,
  Repeat,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PublicProposalData } from '@/hooks/usePublicProposal';
import { formatCurrency, formatDate, daysUntil, validityLabel } from './proposalFormat';

interface ProposalHeroProps {
  data: PublicProposalData;
  onAccept: () => void;
  onReject: () => void;
  onDownloadPdf: () => void;
}

export function ProposalHero({ data, onAccept, onReject, onDownloadPdf }: ProposalHeroProps) {
  const { proposal, tenant, deal, pdf_url: pdfUrl } = data;
  const currency = proposal.currency || 'BRL';
  const remainingDays = daysUntil(proposal.expires_at);
  const urgency = validityLabel(remainingDays);
  // Escassez: destaca quando restam 3 dias ou menos
  const isUrgent = remainingDays !== null && remainingDays <= 3;

  return (
    <section className="proposal-hero-surface proposal-grain proposal-reveal p-6 md:p-10">
      <div className="relative z-10 space-y-8">
        {/* Linha 1: identidade do fornecedor */}
        <div className="flex items-center gap-3">
          {tenant.logo_url ? (
            <img
              src={tenant.logo_url}
              alt={`Logotipo de ${tenant.name ?? 'fornecedor'}`}
              className="h-11 w-11 rounded-xl bg-white/95 object-contain p-1.5"
              loading="lazy"
            />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
              <FileText className="h-5 w-5 opacity-80" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium opacity-90">{tenant.name}</p>
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-55">
              Proposta comercial
            </p>
          </div>
        </div>

        {/* Linha 2: título + destinatário */}
        <div className="space-y-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] tracking-wide opacity-85">
            {proposal.proposal_number} · versão {proposal.version_number}
          </span>
          <h1 className="text-3xl font-semibold leading-tight md:text-[2.75rem] md:leading-[1.1]">
            {proposal.title || `Proposta ${proposal.proposal_number}`}
          </h1>
          {deal.client_name && (
            <p className="text-sm opacity-70">
              Preparada exclusivamente para <span className="font-medium opacity-100">{deal.client_name}</span>
            </p>
          )}
          {proposal.description && (
            <p className="max-w-2xl text-sm leading-relaxed opacity-75">{proposal.description}</p>
          )}
        </div>

        {/* Linha 3: ancoragem de valor + validade */}
        <div className="grid gap-5 rounded-2xl border border-white/10 bg-white/[0.06] p-5 md:grid-cols-[1fr_auto] md:items-end md:p-6">
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-55">
              Investimento total
            </p>
            <p className="text-4xl font-semibold tracking-tight md:text-5xl">
              {formatCurrency(proposal.total, currency)}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs opacity-75">
              {Number(proposal.mrr_total) > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Repeat className="h-3.5 w-3.5" />
                  {formatCurrency(proposal.mrr_total, currency)} recorrentes por mês
                </span>
              )}
              {Number(proposal.discount_total) > 0 && (
                <span className="inline-flex items-center gap-1.5 text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Você economiza {formatCurrency(proposal.discount_total, currency)}
                </span>
              )}
            </div>
          </div>

          {urgency && (
            <div
              className={`inline-flex items-center gap-2 self-start rounded-full px-3.5 py-2 text-xs font-medium md:self-end ${
                isUrgent
                  ? 'bg-amber-400/20 text-amber-200 ring-1 ring-amber-300/40'
                  : 'bg-white/10 text-white/85'
              }`}
            >
              <CalendarClock className="h-4 w-4" />
              {urgency}
            </div>
          )}
        </div>

        {/* Linha 4: CTAs */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {proposal.is_actionable && (
            <>
              <Button
                size="lg"
                onClick={onAccept}
                className="h-12 rounded-xl bg-white px-7 text-base font-semibold text-slate-900 shadow-lg transition-transform hover:scale-[1.02] hover:bg-white"
              >
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Aceitar proposta
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={onReject}
                className="h-12 rounded-xl border border-white/15 px-5 text-white/80 hover:bg-white/10 hover:text-white"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Recusar
              </Button>
            </>
          )}
          {pdfUrl && (
            <Button
              size="lg"
              variant="ghost"
              onClick={onDownloadPdf}
              className="h-12 rounded-xl px-5 text-white/75 hover:bg-white/10 hover:text-white sm:ml-auto"
            >
              <Download className="mr-2 h-4 w-4" />
              Baixar PDF
            </Button>
          )}
        </div>

        {/* Confiança */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-4 text-[11px] opacity-60">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            Aceite eletrônico com registro de data, hora e IP
          </span>
          <span>Emitida em {formatDate(proposal.valid_from)}</span>
          {proposal.expires_at && <span>Válida até {formatDate(proposal.expires_at)}</span>}
        </div>
      </div>
    </section>
  );
}
